import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import React from "react";
import EditIcon from '@mui/icons-material/Edit';
import { EditOutlined, NotesOutlined, PublicOutlined, QrCode2Outlined, SaveOutlined } from "@mui/icons-material";

import { useAuthContext } from "../../context/auth";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useDialogStyles from "../../hooks/useDialogStyles";
import { countryOptions } from "../../constants/countries";
import { DialogHeader, FormSection, SubmitButton, SwitchField } from "../dialogKit";

const MODES = [
  { value: "M1", label: "Mode 1" },
  { value: "M2", label: "Mode 2" },
  { value: "M3A", label: "Mode 3/A" },
];

const Edit = ({
  id,
  mode,
  code,
  countryISO,
  description,
  enabled,
  onUpdated,
}) => {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuthContext();
  const ds = useDialogStyles();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.secondary.main;

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState(countryISO || "");
  const [selectedMode, setSelectedMode] = useState(mode || "");
  const [status, setStatus] = useState(enabled !== false);
  const [codeValue, setCodeValue] = useState(code || "");
  const [descriptionValue, setDescriptionValue] = useState(description || "");

  // Start from the current values every time it opens
  useEffect(() => {
    if (show) {
      setSelectedCountry(countryISO || "");
      setSelectedMode(mode || "");
      setStatus(enabled !== false);
      setCodeValue(code || "");
      setDescriptionValue(description || "");
    }
  }, [show, countryISO, mode, code, description, enabled]);

  const handleEdit = async () => {
    if (!auth) {
      setError("You must be logged in");
      return;
    }

    if (!selectedMode) {
      setError("Please select SIF Mode");
      return;
    }

    if (!selectedCountry) {
      setError("Please select a country");
      return;
    }

    const newCode = codeValue.trim();
    const newDescription = descriptionValue.trim();

    if (!newCode) {
      setError("SIF Code is required");
      return;
    }

    const updatedSIFCode = {
      mode: selectedMode,
      code: newCode,
      countryISO: selectedCountry,
      description: newDescription,
      enabled: status,
    };

    setSaving(true);
    try {
      const response = await axiosPrivate.put(`/api/sifcodes/${id}`, updatedSIFCode);

      if (onUpdated) {
        onUpdated(response.data);
      }

      setError(null);
      setShow(false);
    } catch (error) {
      console.error("Failed to update SIF code:", error);

      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to update SIF code"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    setShow(false);
    setError(null);
  };

  const flag = (iso) => (
    <span className={`fi fi-${iso.toLowerCase()}`} style={{ flexShrink: 0, borderRadius: 2 }} />
  );

  return (
    <>
      <Tooltip title="Edit SIF code">
        <IconButton
          color="secondary"
          size="small"
          style={{ marginRight: 8 }}
          onClick={() => {
            setError(null);
            setShow(true);
          }}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={show}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: ds.paperSx }}
      >
        <DialogHeader
          icon={EditOutlined}
          title="Edit SIF code"
          subtitle={`${MODES.find((m) => m.value === mode)?.label || mode || "SIF"} · ${code || ""}${countryISO ? ` · ${countryISO}` : ""}`}
          onClose={handleClose}
          disabled={saving}
        />

        <DialogContent sx={ds.contentSx}>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
            <FormSection icon={QrCode2Outlined} title="Code" />

            {/* Mode */}
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mb: 0.5 }}>
                SIF Mode
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={selectedMode}
                onChange={(e, v) => { if (v) setSelectedMode(v); }}
                aria-label="SIF Mode"
                sx={{
                  height: 40,
                  p: 0.375,
                  gap: 0.5,
                  borderRadius: 1.5,
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.28)" : theme.palette.neutral[300]}`,
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : theme.palette.background.default,
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: "6px !important",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    color: theme.palette.text.secondary,
                    "&:hover": { backgroundColor: alpha(accent, 0.08) },
                    "&.Mui-selected, &.Mui-selected:hover": {
                      color: accent,
                      backgroundColor: alpha(accent, 0.14),
                      boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.5)}`,
                    },
                  },
                }}
              >
                {MODES.map((m) => (
                  <ToggleButton key={m.value} value={m.value}>{m.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* SIF Code */}
            <TextField
              size="small"
              label="SIF Code"
              placeholder="4521"
              value={codeValue}
              onChange={(e) => setCodeValue(e.target.value)}
              fullWidth
              autoComplete="off"
              sx={ds.fieldSx}
            />

            {/* Status */}
            <SwitchField
              label="Enabled"
              checked={status}
              onChange={setStatus}
              onText="Used to identify targets"
              offText="Kept, but not used"
            />

            <FormSection icon={PublicOutlined} title="Country" />

            {/* Country ISO */}
            <FormControl fullWidth size="small" sx={{ ...ds.fieldSx, minWidth: 0, gridColumn: "1 / -1" }}>
              <InputLabel>Country</InputLabel>

              <Select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                label="Country"
                MenuProps={ds.menuProps}
                renderValue={(selected) => {
                  const country = countryOptions.find((item) => item.code === selected);

                  if (!country) {
                    return "Select Country";
                  }

                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
                      {flag(country.code)}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {country.name} ({country.code})
                      </span>
                    </Box>
                  );
                }}
              >
                {countryOptions.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                      {flag(country.code)}
                      <span>{country.name} ({country.code})</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormSection icon={NotesOutlined} title="Notes" hint="optional" />

            {/* Description */}
            <TextField
              fullWidth
              size="small"
              label="Description"
              placeholder="Enter description"
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              multiline
              minRows={2}
              sx={{ ...ds.fieldSx, gridColumn: "1 / -1" }}
            />
          </Box>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={ds.actionsSx}>
          <Button onClick={handleClose} disabled={saving} sx={ds.cancelButtonSx}>
            Cancel
          </Button>

          <SubmitButton
            label="Save changes"
            busyLabel="Saving…"
            busy={saving}
            onClick={handleEdit}
            icon={<SaveOutlined />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
